import React, { useState, useEffect } from 'react';
import DashboardWrapper from '../../components/DashboardWrapper';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ShieldCheck, ShieldAlert, AlertCircle, AlertTriangle, 
  CheckCircle, RefreshCw, Users, Package, Building2,
  ChevronDown, ChevronRight, Wrench
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminAudit = () => {
  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    users: true,
    products: true
  });

  const runAudit = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/audit/partner-integrity');
      setAuditResult(response.data);
    } catch (error) {
      toast.error('Erreur lors de l\'audit');
    } finally {
      setLoading(false);
    }
  };

  const fixNonPartners = async () => {
    if (!window.confirm('Êtes-vous sûr ? Cette action mettra partner_id à null pour tous les utilisateurs non-partenaires.')) {
      return;
    }
    setFixing(true);
    try {
      const response = await api.post('/admin/audit/fix-non-partners');
      toast.success(`${response.data.modified_count} utilisateur(s) corrigé(s)`);
      await runAudit(); // Re-run audit after fix
    } catch (error) {
      toast.error('Erreur lors de la correction');
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => {
    runAudit();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'WARNING':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'WARNING':
        return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
  };

  return (
    <DashboardWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit d'Intégrité</h1>
            <p className="text-muted-foreground">
              Vérification des relations User ↔ Partner ↔ Product
            </p>
          </div>
          <Button 
            onClick={runAudit} 
            disabled={loading}
            data-testid="run-audit-button"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analyse...' : 'Relancer l\'audit'}
          </Button>
        </div>

        {auditResult && (
          <>
            {/* Status Card */}
            <Card className={auditResult.overall_status === 'PASS' ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  {auditResult.overall_status === 'PASS' ? (
                    <ShieldCheck className="h-12 w-12 text-green-600" />
                  ) : (
                    <ShieldAlert className="h-12 w-12 text-red-600" />
                  )}
                  <div>
                    <h2 className="text-xl font-bold">
                      {auditResult.overall_status === 'PASS' 
                        ? '✅ Intégrité OK' 
                        : '⚠️ Problèmes détectés'
                      }
                    </h2>
                    <p className="text-muted-foreground">
                      Audit du {new Date(auditResult.audit_timestamp).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="ml-auto flex gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {auditResult.summary?.total_critical_issues || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Critiques</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-600">
                        {auditResult.summary?.total_warning_issues || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Avertissements</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommendations */}
            {auditResult.recommendations && auditResult.recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recommandations</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {auditResult.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-lg">{rec.startsWith('✅') ? '' : rec.startsWith('⚠️') ? '' : ''}</span>
                        <span className="text-sm">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* User Audit */}
            <Card>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => toggleSection('users')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedSections.users ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    <Users className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">Audit Utilisateurs</CardTitle>
                    {auditResult.user_audit?.issues_count > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {auditResult.user_audit.issues_count} problème(s)
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{auditResult.user_audit?.total_users || 0} utilisateurs</span>
                    <span>{auditResult.user_audit?.total_partenaires || 0} partenaires</span>
                  </div>
                </div>
              </CardHeader>
              {expandedSections.users && (
                <CardContent>
                  {auditResult.user_audit?.issues?.length > 0 ? (
                    <div className="space-y-2">
                      {auditResult.user_audit.issues.map((issue, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${getSeverityColor(issue.severity)}`}
                        >
                          <div className="flex items-center gap-2">
                            {getSeverityIcon(issue.severity)}
                            <Badge variant="outline" className="text-xs">
                              {issue.type}
                            </Badge>
                            <span className="font-medium">{issue.email}</span>
                            {issue.pseudo && (
                              <span className="text-muted-foreground">({issue.pseudo})</span>
                            )}
                          </div>
                          <p className="text-sm mt-1 ml-6">{issue.message}</p>
                          {issue.partner_id && (
                            <p className="text-xs mt-1 ml-6 font-mono bg-white/50 px-2 py-1 rounded inline-block">
                              partner_id: {issue.partner_id}
                            </p>
                          )}
                        </div>
                      ))}
                      
                      {/* Fix button for warnings */}
                      {auditResult.user_audit.warning_count > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <Button 
                            variant="outline" 
                            onClick={fixNonPartners}
                            disabled={fixing}
                            data-testid="fix-non-partners-button"
                          >
                            <Wrench className="h-4 w-4 mr-2" />
                            {fixing ? 'Correction...' : 'Corriger les non-partenaires avec partner_id'}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span>Aucun problème détecté</span>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Product Audit */}
            <Card>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => toggleSection('products')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedSections.products ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    <Package className="h-5 w-5 text-purple-600" />
                    <CardTitle className="text-lg">Audit Produits</CardTitle>
                    {auditResult.product_audit?.issues_count > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {auditResult.product_audit.issues_count} orphelin(s)
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{auditResult.product_audit?.total_products || 0} produits</span>
                    <span>{auditResult.product_audit?.products_with_partner || 0} avec partenaire</span>
                  </div>
                </div>
              </CardHeader>
              {expandedSections.products && (
                <CardContent>
                  {auditResult.product_audit?.issues?.length > 0 ? (
                    <div className="space-y-2">
                      {auditResult.product_audit.issues.map((issue, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${getSeverityColor(issue.severity)}`}
                        >
                          <div className="flex items-center gap-2">
                            {getSeverityIcon(issue.severity)}
                            <Badge variant="outline" className="text-xs">
                              {issue.type}
                            </Badge>
                            <span className="font-medium">{issue.intitule}</span>
                          </div>
                          <p className="text-sm mt-1 ml-6">{issue.message}</p>
                          <p className="text-xs mt-1 ml-6 font-mono bg-white/50 px-2 py-1 rounded inline-block">
                            partner_id: {issue.partner_id}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span>Aucun produit orphelin</span>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Partners Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-lg">Partenaires Enregistrés</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>
                    {auditResult.user_audit?.valid_partner_ids_count || 0} partenaire(s) valide(s) dans la base
                  </span>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!auditResult && !loading && (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">Cliquez sur "Relancer l'audit" pour analyser les données</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default AdminAudit;
